/**
 * useEventProductStreaming Hook
 * React hook for auto-paginated streaming of event products.
 * Fetches all products for an event in batches, updating the UI progressively.
 */
import { useState, useCallback, useRef } from 'react';
import { EventProductStreamingService } from '../../services/vendorcatalog/EventProductStreamingService';
import { useVendorCatalogShopService } from './services/useVendorCatalogServices';
/**
 * React hook for streaming event products with auto-pagination.
 * Loads products in batches from the server, progressively updating the UI
 * as each batch arrives.
 *
 * @param eventId - The event ID to fetch products for
 * @param batchSize - Number of products per batch (default: 10)
 * @returns Hook return object with products, loading state, and control functions
 *
 * @example
 * ```tsx
 * function EventProductList({ eventId }: { eventId: number }) {
 *   const {
 *     products,
 *     isLoading,
 *     isDone,
 *     progress,
 *     loadedCount,
 *     totalCount,
 *     error,
 *     load,
 *     reset,
 *   } = useEventProductStreaming(eventId, 10);
 *
 *   useEffect(() => {
 *     load();
 *   }, [eventId]);
 *
 *   return (
 *     <div>
 *       {isLoading && <ProgressBar value={progress} />}
 *       {error && <p>Error: {error.message}</p>}
 *       <p>{loadedCount} of {totalCount} products loaded</p>
 *       <div className="product-grid">
 *         {products.map(product => (
 *           <ProductCard key={product.productID} product={product} />
 *         ))}
 *       </div>
 *       {isDone && <p>All products loaded!</p>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEventProductStreaming(eventId, batchSize = 10) {
    const shopService = useVendorCatalogShopService();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loadedCount, setLoadedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState(null);
    const abortRef = useRef(false);
    const load = useCallback(async () => {
        // Reset state before loading
        setIsLoading(true);
        setIsDone(false);
        setProgress(0);
        setProducts([]);
        setLoadedCount(0);
        setTotalCount(0);
        setError(null);
        abortRef.current = false;
        try {
            const streamingService = new EventProductStreamingService(shopService);
            let accumulated = [];
            for await (const chunk of streamingService.streamEventProducts(eventId, batchSize)) {
                if (abortRef.current)
                    break;
                accumulated = [...accumulated, ...chunk.items];
                const currentTotal = chunk.totalCount;
                setProducts(accumulated);
                setLoadedCount(accumulated.length);
                setTotalCount(currentTotal);
                setProgress(currentTotal > 0
                    ? Math.min(99, Math.round((accumulated.length / currentTotal) * 100))
                    : 0);
            }
            if (!abortRef.current) {
                setProgress(100);
                setIsDone(true);
            }
        }
        catch (err) {
            const loadError = err instanceof Error ? err : new Error(String(err));
            setError(loadError);
        }
        finally {
            setIsLoading(false);
        }
    }, [shopService, eventId, batchSize]);
    const reset = useCallback(() => {
        abortRef.current = true;
        setProducts([]);
        setIsLoading(false);
        setIsDone(false);
        setProgress(0);
        setLoadedCount(0);
        setTotalCount(0);
        setError(null);
    }, []);
    return {
        products,
        isLoading,
        isDone,
        progress,
        loadedCount,
        totalCount,
        error,
        load,
        reset,
    };
}
//# sourceMappingURL=useEventProductStreaming.js.map